import { animate } from 'motion/mini';
import { motionEase, useMotionPolicy } from '../components/Motion';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { TextHoverEffect } from '../components/TextHoverEffect';
import { OfficialPartners } from '../components/OfficialPartners';

const comparison = [
  ['Monthly price', '$24.99', '$79'],
  ['Media storage', '1 TB', '500 GB'],
  ['Storage overage', 'Uploads stop at the plan limit', '$0.10 per GB'],
  ['Monthly bandwidth', 'Unlimited', '5 TB'],
  ['Bandwidth overage', 'None', '$4 per TB'],
  ['Log storage', '500 GB', '250 GB'],
  ['Log retention', 'No forced expiry', '180 days'],
  ['S3-compatible access', 'Included', 'Not listed'],
];

const logCapabilities = [
  {
    title: 'Keep services separate',
    body: 'Give production, staging and each service its own stream. Move between them without mixing unrelated events.',
  },
  {
    title: 'Search messages and JSON',
    body: 'Match plain text or values inside the payload, then narrow the result by time and event type.',
  },
  {
    title: 'Trace one entity',
    body: 'Use an entity ID to follow the same player, transaction or server across every related event.',
  },
  {
    title: 'Retain what you need',
    body: 'Your plan sets the storage allowance. There is no separate timer deleting useful history early.',
  },
];

export function Landing() {
  const landingRef = useRef<HTMLDivElement>(null);

  const heroPlayed = useRef(false);
  const { instant } = useMotionPolicy();
  useEffect(() => {
    const landing = landingRef.current;
    if (!landing) return;

    if (instant || typeof IntersectionObserver === 'undefined') {
      landing.dataset.motion = 'reduced';
      return;
    }

    landing.dataset.motion = 'enhanced';
    const animations: ReturnType<typeof animate>[] = [];
    if (!heroPlayed.current) landing.querySelectorAll<HTMLElement>('.landing-hero h1 > span').forEach((line, index) => {
      animations.push(animate(line, { opacity: [0, 1], transform: ['translateY(18px)', 'none'] },
        { duration: 0.55, delay: index * 0.07, ease: motionEase }));
    });
    heroPlayed.current = true;
    const revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry, index) => {
          if (!entry.isIntersecting) return;
          const target = entry.target as HTMLElement;
          if (!target.classList.contains('is-visible')) {
            target.classList.add('is-visible');
            animations.push(animate(target, { opacity: [0, 1], transform: ['translateY(20px)', 'none'] },
              { duration: 0.5, delay: Math.min(index * 0.055, 0.165), ease: motionEase }));
          }
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.14 },
    );

    landing.querySelectorAll<HTMLElement>('[data-reveal]').forEach((element) => {
      revealObserver.observe(element);
    });

    const logsSection = landing.querySelector<HTMLElement>('.logs-copy-section__inner');
    const logSteps = landing.querySelectorAll<HTMLElement>('[data-log-step]');
    const logStepObserver = new IntersectionObserver(
      (entries) => {
        const visibleStep = entries
          .filter((entry) => entry.isIntersecting)
          .sort((left, right) => right.intersectionRatio - left.intersectionRatio)[0];
        if (visibleStep && logsSection) {
          logsSection.dataset.activeStep =
            (visibleStep.target as HTMLElement).dataset.logStep ?? '0';
        }
      },
      { rootMargin: '-28% 0px -42% 0px', threshold: [0.2, 0.5, 0.8] },
    );
    logSteps.forEach((step) => logStepObserver.observe(step));

    return () => {
      revealObserver.disconnect();
      logStepObserver.disconnect();
      animations.forEach(animation => animation.cancel());
    };
  }, [instant]);

  return (
    <div className="landing-page" ref={landingRef}>
      <section className="landing-hero" id="product">
        <div className="shell landing-hero__inner">
          <h1 aria-label="Storage and delivery for the services you actually run.">
            <span>Storage and delivery</span>
            <span>for the services</span>
            <span>you actually run.</span>
          </h1>
          <p data-reveal>
            Store files, serve media, connect S3 clients and search application logs from one
            account.
          </p>
          <div className="landing-hero__actions" data-reveal>
            <Link className="button button--accent" to="/signup">
              Start free <ArrowRight />
            </Link>
            <Link className="button button--quiet" to="/pricing">
              View pricing
            </Link>
          </div>
          <dl className="landing-hero__facts" data-reveal>
            <div>
              <dt>Lite</dt>
              <dd>€4.99 monthly</dd>
            </div>
            <div>
              <dt>Included storage</dt>
              <dd>250 GB</dd>
            </div>
            <div>
              <dt>Delivery</dt>
              <dd>No bandwidth meter</dd>
            </div>
          </dl>
          <div className="landing-reliability" data-reveal aria-label="Infrastructure reliability">
            <p>
              <strong>99.99%</strong>
              <span>Edge CDN uptime</span>
            </p>
            <p>
              <strong>99.99999999%</strong>
              <span>Data durability</span>
            </p>
          </div>
        </div>
      </section>

      <section className="manifesto-section" aria-labelledby="manifesto-heading">
        <div className="shell manifesto-section__inner">
          <h2 id="manifesto-heading" data-reveal>
            What do we have to say?
          </h2>
          <div className="manifesto-statements">
            <blockquote className="manifesto-statement manifesto-statement--pricing" data-reveal>
              <p>
                “We&apos;re sick of corporate{' '}
                <span className="manifesto-tail">
                  <span className="manifesto-strike">bullshit</span>.”
                </span>
              </p>
              <footer>
                Inflated pricing, arbitrary feature paywalls, and “Contact Sales” buttons make us
                sick. We don&apos;t care whether you&apos;re a solo developer or a massive gaming
                community. You get the same edge network, the same S3 API, and the same respect. No
                feature tiers, no sales calls, no games.
              </footer>
            </blockquote>
            <blockquote className="manifesto-statement manifesto-statement--safety" data-reveal>
              <p>“Your data is safe with us.”</p>
              <footer>
                BCKT storage nodes run from the ISO 27001-certified Interxion AMS5 facility in
                Amsterdam. Our storage layer is engineered for 10 nines of annual durability.
                Private objects stay private, and transfers are encrypted in transit.
              </footer>
            </blockquote>
          </div>
        </div>
      </section>

      <section className="capability-section" id="storage">
        <div className="shell capability-section__grid">
          <div className="capability-section__statement">
            <h2>Files in. URLs out.</h2>
            <p>
              Upload through the dashboard, REST API or S3. Choose public delivery or expiring
              private links per object.
            </p>
            <Link className="text-link" to="/docs#uploads">
              Read the upload guide <ArrowRight />
            </Link>
          </div>
          <dl className="capability-list">
            <div data-reveal>
              <dt>Direct uploads</dt>
              <dd>Send files up to 20 GB (up to 100 MB on single upload) without staging them on the control server.</dd>
            </div>
            <div data-reveal>
              <dt>S3 gateway</dt>
              <dd>Connect SDKs, backup tools and existing S3-compatible clients.</dd>
            </div>
            <div data-reveal>
              <dt>Public or private</dt>
              <dd>Publish a permanent CDN URL or create a signed link that expires in one hour.</dd>
            </div>
            <div data-reveal>
              <dt>Deletion that finishes</dt>
              <dd>
                Remove the database record, origin object, object versions and cached response.
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="logs-copy-section" id="logs">
        <div className="shell logs-copy-section__inner" data-active-step="0">
          <div className="logs-copy-section__sticky">
            <div>
              <h2>Logs you can search when something breaks.</h2>
              <p>
                Send JSON events, then filter by time, message, event type or entity from Log
                Observatory.
              </p>
            </div>
            <div className="logs-story-progress" aria-hidden="true">
              {logCapabilities.map((capability, index) => (
                <span key={capability.title} data-progress-step={index} />
              ))}
            </div>
            <Link className="button button--quiet" to="/docs#ingest">
              Send your first event <ArrowRight />
            </Link>
          </div>
          <div className="logs-capabilities" aria-label="Log Observatory capabilities">
            {logCapabilities.map((capability, index) => (
              <article key={capability.title} data-log-step={index} data-reveal>
                <h3>{capability.title}</h3>
                <p>{capability.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="capability-section" id="child-safety" aria-labelledby="child-safety-heading">
        <div className="shell capability-section__grid">
          <div className="capability-section__statement" data-reveal>
            <h2 id="child-safety-heading">CSAM is blocked, banned, and reported.</h2>
            <p>
              Known child sexual abuse material never reaches the CDN. The account is banned,
              encrypted evidence is preserved, and a CyberTip is filed.
            </p>
          </div>
          <dl className="capability-list">
            <div data-reveal>
              <dt>
                <a href="https://www.microsoft.com/en-us/photodna" rel="noreferrer" target="_blank">
                  Microsoft PhotoDNA
                </a>
              </dt>
              <dd>
                Images and video frames are matched against PhotoDNA hashes on upload. A match is
                never published.
              </dd>
            </div>
            <div data-reveal>
              <dt>
                <a href="https://www.missingkids.org/home" rel="noreferrer" target="_blank">
                  NCMEC CyberTipline
                </a>
              </dt>
              <dd>
                Confirmed cases keep encrypted evidence and the uploader&apos;s technical
                identifiers, then a report is sent to the National Center for Missing &amp;
                Exploited Children.
              </dd>
            </div>
          </dl>
          <div style={{ marginTop: "24px", paddingTop: "20px", borderTop: "1px solid rgba(255,255,255,0.08)", fontSize: "14px", color: "var(--color-muted)", gridColumn: "1 / -1" }} data-reveal>
            Do you need to report CSAM related content? <a href="/report?category=child_safety" style={{ color: "var(--color-accent)", textDecoration: "underline", fontWeight: 600 }}>Click here</a> to file an immediate priority report.
          </div>
        </div>
      </section>

      <section className="comparison-section comparison-section--focused">
        <div className="shell comparison-layout">
          <div className="comparison-copy" data-reveal>
            <h2>Compare the capacity, not the pitch.</h2>
            <p>
              Community Pro is the closest BCKT comparison to the Fivemanage Network plan for a
              community using both media storage and logs.
            </p>
            <Link className="text-link" to="/pricing">
              Compare every BCKT plan <ArrowRight />
            </Link>
          </div>
          <div data-reveal>
            <div className="comparison-table comparison-table--two">
              <div className="comparison-table__head">
                <span>Plan limits</span>
                <strong>BCKT Community Pro</strong>
                <strong>Fivemanage Network</strong>
              </div>
              {comparison.map((row) => (
                <div className="comparison-table__row" key={row[0]}>
                  <strong>{row[0]}</strong>
                  <span>
                    <Check />
                    {row[1]}
                  </span>
                  <span>{row[2]}</span>
                </div>
              ))}
            </div>
            <p className="comparison-note">
              Fivemanage figures reflect the Network plan details supplied on 31 August 2026.
              Provider pricing and features can change.
            </p>
            <p className="comparison-competitor-extra">
              Fivemanage Network also lists a player upload portal, custom CDN domains and CDN
              analytics marked as coming soon.
            </p>
          </div>
        </div>
      </section>

      <div className="shell"><OfficialPartners /></div>
      <section className="closing-cta" id="start" data-reveal>
        <div className="shell closing-cta__content">
          <p>Start with 10 GB of media and 5 GB of logs.</p>
          <div>
            <Link className="button button--light" to="/signup">
              Create an account
            </Link>
            <Link className="text-link" to="/docs">
              Read the documentation <ArrowRight />
            </Link>
          </div>
        </div>
        <div className="brand-signature">
          <TextHoverEffect text="BCKT" />
        </div>
      </section>
    </div>
  );
}
